import { SITE_URL, PODCAST_URL } from '../../../utils/constants';

export type BlogPostMeta = {
    author: string;
    datePublished: string;
};

export type PodcastEpisodeMeta = {
    episodeNumber: number;
    datePublished?: string;
    guest?: string;
    business?: string;
    spotify?: string;
    apple?: string;
    youtube?: string;
};

export type ServiceMeta = {
    name: string;
    description?: string;
    serviceType?: string;
    areaServed?: string | string[];
    audience?: string;
};

export type FAQPageMeta = {
    name?: string;
    anchorId?: string;
    questions: Array<{
        title: string;
        p: string;
    }>;
};

type ReturnLdOptions = {
    /**
     * Override the canonical site root used for page-level URLs (WebPage, BreadcrumbList,
     * PodcastEpisode, etc.). Defaults to the studio's SITE_URL with locale prefix.
     * The podcast app passes PODCAST_URL since it lives at cuandoelriosuena.com with no /es prefix.
     * Organization-level URLs (logo, sameAs, publisher) always use SITE_URL — Acueducto is the
     * publisher in both apps.
     */
    siteRoot?: string;
    /**
     * Full canonical URL for the current page (e.g. `https://acueducto.com/es/portafolio/borgatta`).
     * When provided, overrides the default `${siteRoot}${asPath}` page URL construction. Necessary
     * for studio pages where middleware rewrites a user-facing slug (`/es/portafolio/<x>`) to an
     * internal path (`/es/work/<x>`); `router.asPath` returns the post-rewrite path which doesn't
     * exist publicly. Without this override, JSON-LD WebPage/BreadcrumbList URLs leak the
     * non-canonical internal path and contradict the `<link rel="canonical">` tag.
     */
    canonicalUrl?: string;
    service?: ServiceMeta;
    faqPage?: FAQPageMeta;
};

const returnLd = (
    locale: string | undefined,
    asPath: string,
    description: string | undefined,
    title: string | undefined,
    headerTitle: string,
    image?: { fileName: string; alt: string | undefined },
    blogPost?: BlogPostMeta,
    podcastEpisode?: PodcastEpisodeMeta,
    options: ReturnLdOptions = {},
) => {
    const isSpanish = locale === "es";
    const localePrefix = isSpanish ? "/es" : "";
    const siteRoot = options.siteRoot ?? `${SITE_URL}${localePrefix}`;
    // Prefer the explicit canonical URL when the caller provides it (avoids leaking the
    // post-rewrite internal path from `router.asPath`). Fall back to the legacy construction
    // for callers that don't pass one. Root path collapses to `siteRoot` (no trailing slash).
    const pageUrl = options.canonicalUrl
        ?? (asPath === "/" ? siteRoot : `${siteRoot}${asPath}`);
    const webPageId = `${pageUrl}#webpage`;
    const primaryImageId = `${pageUrl}#primaryimage`;
    const serviceId = `${pageUrl}#service`;

    const serviceSchema = options.service ? {
        "@type": "Service",
        "@id": serviceId,
        url: pageUrl,
        name: options.service.name,
        description: options.service.description ?? description,
        serviceType: options.service.serviceType,
        provider: { "@id": `${SITE_URL}/#organization` },
        areaServed: options.service.areaServed,
        audience: options.service.audience ? {
            "@type": "Audience",
            audienceType: options.service.audience,
        } : undefined,
        image: { "@id": primaryImageId },
        mainEntityOfPage: { "@id": webPageId },
        inLanguage: isSpanish ? "es" : "en",
    } : null;

    const faqPageSchema = options.faqPage ? {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        url: options.faqPage.anchorId
            ? `${pageUrl}#${options.faqPage.anchorId}`
            : pageUrl,
        name: options.faqPage.name ?? `${headerTitle} FAQ`,
        isPartOf: { "@id": webPageId },
        mainEntity: options.faqPage.questions.map((question) => ({
            "@type": "Question",
            name: question.title,
            acceptedAnswer: {
                "@type": "Answer",
                text: question.p,
            },
        })),
        inLanguage: isSpanish ? "es" : "en",
    } : null;

    const breadcrumbItems = [
        {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteRoot,
        },
        asPath.includes("/portafolio") || asPath.includes("/work") ? ({
            "@type": "ListItem",
            position: 2,
            name: isSpanish ? "Portafolio" : "Work",
            item: `${SITE_URL}${isSpanish ? "/es/portafolio" : "/work"}`,
        }) : null,
        asPath.includes("/blog") ? ({
            "@type": "ListItem",
            position: 2,
            name: "Blog",
            item: `${SITE_URL}/es/blog`,
        }) : null,
        asPath !== "/" && asPath !== "/blog" && asPath !== "/portafolio" && asPath !== "/work" ? ({
            "@type": "ListItem",
            position: asPath.includes("/portafolio") || asPath.includes("/work") || asPath.includes("/articles") ? 3 : 2,
            name: asPath.includes("/blog") ? title : (siteRoot === PODCAST_URL ? title : headerTitle),
            item: pageUrl,
        }) : null,
    ].filter(Boolean);

    const structure = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "@id": `${SITE_URL}/#organization`,
                name: "Acueducto",
                url: SITE_URL,
                sameAs: [
                    "https://www.facebook.com/acueducto.studio",
                    "https://www.instagram.com/acueducto.studio/",
                    "https://www.linkedin.com/company/acueductostudio/",
                    "https://twitter.com/acueductostudio",
                    "https://www.tiktok.com/@acueducto.studio",
                    "https://www.youtube.com/@acueducto.studio",
                    "https://open.spotify.com/show/2YLB7SOeJsLp5DtDuIwX8t",
                    "https://podcasts.apple.com/us/podcast/cuando-el-rio-suena/id1500473556",
                ],
                logo: {
                    "@type": "ImageObject",
                    "@id": `${SITE_URL}/#logo`,
                    contentUrl: `${SITE_URL}/assets/img/og/acueducto.png`,
                    width: 1200,
                    height: 1200,
                    caption: "Acueducto",
                    inLanguage: isSpanish ? "es" : "en",
                },
                image: { "@id": `${SITE_URL}/#logo` },
            },
            image ? {
                "@type": "ImageObject",
                "@id": primaryImageId,
                contentUrl: image.fileName.includes('images.prismic.io')
                    ? image.fileName
                    : `${SITE_URL}/assets/img/og/${image.fileName}`,
                width: 1200,
                height: 1200,
                caption: image.alt,
                inLanguage: isSpanish ? "es" : "en",
            } : {
                "@type": "ImageObject",
                "@id": primaryImageId,
                contentUrl: `${SITE_URL}/assets/img/og/acueducto.png`,
                width: 1200,
                height: 1200,
                caption: "Acueducto",
                inLanguage: isSpanish ? "es" : "en",
            },
            {
                "@type": "WebSite",
                "@id": `${siteRoot}#website`,
                url: siteRoot,
                name: "Acueducto",
                description: isSpanish
                    ? "Desarrollamos estrategias digitales, apps web y móviles, productos digitales y campañas de marketing digital. Elevemos tu negocio con tecnología digital."
                    : "We partner with innovators around the globe to develop digital strategies and experiences that tell compelling stories, inspire communities and build meaningful bonds.",
                publisher: {
                    "@id": `${SITE_URL}/#organization`,
                },
                inLanguage: isSpanish ? "es" : "en",
            },
            {
                "@type": "WebPage",
                "@id": webPageId,
                url: pageUrl,
                name: title,
                isPartOf: { "@id": `${siteRoot}#website` },
                about: { "@id": `${SITE_URL}/#organization` },
                primaryImageOfPage: { "@id": primaryImageId },
                mainEntity: serviceSchema ? { "@id": serviceId } : undefined,
                description: description,
                inLanguage: isSpanish ? "es" : "en",
            },
            {
                "@type": "BreadcrumbList",
                "@id": `${siteRoot}#breadcrumbs`,
                itemListElement: breadcrumbItems,
            },
            blogPost ? ({
                "@type": "BlogPosting",
                "@id": `${pageUrl}#blogposting`,
                headline: title,
                description,
                author: { "@type": "Person", name: blogPost.author },
                datePublished: blogPost.datePublished,
                dateModified: blogPost.datePublished,
                image: { "@id": primaryImageId },
                mainEntityOfPage: { "@id": webPageId },
                publisher: { "@id": `${SITE_URL}/#organization` },
                inLanguage: isSpanish ? "es" : "en",
            }) : null,
            podcastEpisode ? ({
                "@type": "PodcastEpisode",
                "@id": `${pageUrl}#episode`,
                url: pageUrl,
                name: title,
                description,
                datePublished: podcastEpisode.datePublished,
                episodeNumber: podcastEpisode.episodeNumber,
                partOfSeries: {
                    "@type": "PodcastSeries",
                    "@id": `${PODCAST_URL}#series`,
                    name: "Cuando el río suena",
                    url: PODCAST_URL,
                },
                ...(podcastEpisode.guest && {
                    actor: [{
                        "@type": "Person",
                        name: podcastEpisode.guest,
                        ...(podcastEpisode.business && {
                            affiliation: { "@type": "Organization", name: podcastEpisode.business },
                        }),
                    }],
                }),
                associatedMedia: [
                    podcastEpisode.spotify ? { "@type": "AudioObject", contentUrl: podcastEpisode.spotify } : null,
                    podcastEpisode.apple ? { "@type": "AudioObject", contentUrl: podcastEpisode.apple } : null,
                ].filter(Boolean),
                image: { "@id": primaryImageId },
                inLanguage: "es",
            }) : null,
            serviceSchema,
            faqPageSchema,
        ].filter(Boolean),
    };
    return JSON.stringify(structure).replace(/</g, '\\u003c');
};

export default returnLd;
